import type { z } from "zod/v4";
import { skipToken, type SkipToken, type QueryFunction } from "@tanstack/react-query";
import type { TenantId } from "@visioni/a3s-schema";
import type { QuerySchema, ResourceConfig, ResourceParams, ResourceScope } from "../resource";
import { expandEndpoint, type EndpointConfig, type ScopedPathParams } from "../endpoint";
import { createQueryKey } from "./query-key";

/**
 * Runtime configuration consumed by every query/mutation hook in this package.
 *
 * `tenant` is intentionally optional: queries return `skipToken` until both the
 * tenant id and the realm-specific API base URL are known, which allows
 * components to mount before authentication has resolved without throwing.
 *
 * `authenticatedFetch` is the realm-aware fetch that injects the bearer token
 * (and refreshes it when needed). All HTTP traffic from queries and mutations
 * flows through this single function so that auth concerns stay out of the
 * per-feature endpoint definitions.
 */
export interface ApiConfig {
  tenant?: { tenantId: TenantId; apiBaseUrl: string } | undefined;
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

/**
 * Creates a React Query options factory from a GET endpoint.
 *
 * @example
 * const listSnapshots = defineEndpoint(snapshotsResource, {
 *   responseSchema: ListSnapshotsResponseSchema,
 * });
 * const listSnapshotsQuery = queryFor(listSnapshots);
 * // usage: listSnapshotsQuery(apiConfig, pathParams)
 */
export function queryFor<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    "GET",
    TResponseSchema,
    undefined,
    TQuerySchema
  >,
) {
  type TResponse = z.output<TResponseSchema>;
  type TQuery = TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined;
  type TExtraPath = z.output<TResourcePathSchema>;

  // Combined path: scoped base params (minus tenantId) + extra params from template
  // For camera scope: Omit<BasePathParams, "tenantId"> (siteId, assetCameraKey)
  // For tenant scope: {} (no additional params)
  type TPathInput = Omit<ScopedPathParams<TScope>, "tenantId"> & TExtraPath;
  type TFullPath = ScopedPathParams<TScope> & TExtraPath;

  // Query key type is inferred from createQueryKey
  type TQueryKeyValue = ReturnType<typeof createQueryKey<TResource, TResourcePathSchema, TScope, TQuerySchema>>;
  type TQueryKey = readonly [TQueryKeyValue] | [];
  type QueryOptions = { queryKey: TQueryKey; queryFn: QueryFunction<TResponse, TQueryKey> | SkipToken };

  const { responseSchema } = endpoint;

  return (apiConfig: ApiConfig, pathParams: TPathInput, queryParams?: TQuery): QueryOptions => {
    const { tenant, authenticatedFetch } = apiConfig;
    if (!tenant) {
      return {
        queryKey: [],
        queryFn: skipToken,
      };
    }
    const { tenantId, apiBaseUrl } = tenant;

    // Cast needed: TypeScript can't infer spread produces TFullPath
    const fullPath = { tenantId, ...pathParams } as TFullPath;

    const queryKey = createQueryKey(endpoint, {
      path: fullPath,
      query: queryParams,
    });

    const queryFn: QueryFunction<TResponse, TQueryKey> = async ({ signal }) => {
      const url = `${apiBaseUrl}${expandEndpoint(endpoint, { path: fullPath, query: queryParams })}`;

      const response = await authenticatedFetch(url, { signal });

      if (!response.ok) {
        let errorMessage = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) {
                errorMessage = json.message;
              }
            } catch {
              errorMessage = text;
            }
          }
        } catch {
          // failed to read response body, use default message
        }
        throw new Error(errorMessage);
      }

      const data: unknown = await response.json();
      return responseSchema.parse(data);
    };

    return {
      queryKey: [queryKey] as const,
      queryFn,
    };
  };
}
